import { Component, computed, effect, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { NzModalRef, NzModalService } from 'ng-zorro-antd/modal';
import { ViewContainerRef } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzRadioModule } from 'ng-zorro-antd/radio';
import { NzDescriptionsModule } from 'ng-zorro-antd/descriptions';
import { NzColDirective, NzRowDirective } from 'ng-zorro-antd/grid';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import { NzAvatarModule } from 'ng-zorro-antd/avatar';
import { NzSpaceModule } from 'ng-zorro-antd/space';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzMessageService } from 'ng-zorro-antd/message';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { UserOrganization } from '@app/typedef/define/user/UserOrganization';
import { UserSettings } from '@app/typedef/define/user/UserSettings';
import { AccountService } from '@app/service/account.service';
import { UserOrganizationService } from '@app/service/user.organization.service';
import {
  OrganizationCreateDialogComponent,
  OrganizationCreatePayload,
} from '@app/pages/main/organization/create/organization.create.dialog.component';

/**
 * 顶栏「账号」的**组织切换**对话框：点一下弹窗、列出可选组织（个人组织 + 服务器真实组织），
 * 点卡片切当前组织并关窗。布局照 `account.component.html` 的组织内容区那张卡网格。
 *
 * 与语言/主题对话框同一套写法（`inject(NzModalRef)`、`cancel/ok` 关窗、卡片 `[nzHoverable]`、
 * 当前项打勾）。
 *
 * **注意这里不是账号页，而是它的「组织快速切换」**：所以顶上放一条当前用户条 + 一个「账号」链接
 * （退出/设置都在账号页），保证把顶栏「账号」改成弹窗后、账号页入口不丢。
 *
 * 组织列表逻辑与 AccountComponent 同构（个人组织打头、功能未启用只有一张、启用了才拉真实组织、
 * `setOrganization` 切当前组织）——这里按「对话框」的尺寸重铺，不复刻账号页那张页头的其余部分。
 */
@Component({
  selector: 'app-organization-change',
  templateUrl: './organization.change.component.html',
  styleUrl: './organization.change.component.less',
  imports: [
    NzButtonModule,
    NzCardModule,
    NzRadioModule,
    NzDescriptionsModule,
    NzTooltipModule,
    FormsModule,
    NzRowDirective,
    NzColDirective,
    NzIconModule,
    NzAvatarModule,
    NzSpaceModule,
    NzSpinModule,
    TranslatePipe,
  ],
  providers: [NzModalService],
})
export class OrganizationChangeComponent {
  readonly account = inject(AccountService);
  private readonly service = inject(UserOrganizationService);
  private readonly router = inject(Router);
  private readonly msg = inject(NzMessageService);
  private readonly translate = inject(TranslateService);
  private readonly modal = inject(NzModalService);
  private readonly viewContainerRef = inject(ViewContainerRef);
  readonly #modalRef = inject(NzModalRef);

  /** 组织功能开关（取当前设置；功能未启用时列表里只有个人组织） */
  protected organizationEnabled = signal(this.account.userSettings().organizationEnabled);

  /** 服务器真实组织（不含虚拟个人组织）。功能未启用时保持空。 */
  protected organizations = signal<UserOrganization[]>([]);

  protected loading = signal(false);

  /** 复制用户 ID 中间态（防连点）。 */
  protected copying = signal(false);

  /** 列表第一张永远是个人组织（虚拟），启用了才追服务器真实组织。 */
  protected readonly displayOrganizations = computed(() => {
    const list = [this.account.personalOrganization()];
    if (this.organizationEnabled()) {
      list.push(...this.organizations());
    }
    return list;
  });

  constructor() {
    // service 端把 userSettings 换掉时（保存失败回滚、别处改了设置）跟上 —— 让开关如实反映真实状态
    effect(() => {
      const enabled = this.account.userSettings().organizationEnabled;
      if (this.organizationEnabled() !== enabled) {
        this.organizationEnabled.set(enabled);
      }
    });

    // 组织功能启用才拉真实组织（与 AccountComponent 同口径）；未启用不用拉（列表里只有个人组织）
    effect(() => {
      if (this.account.userSettings().organizationEnabled) {
        this.loadOrganizations();
      }
    });
  }

  /** 拉服务器真实组织列表。 */
  private loadOrganizations(): void {
    this.loading.set(true);
    this.service.getOrganizations().subscribe({
      next: (data) => {
        this.organizations.set(data);
        this.loading.set(false);
      },
      error: (error) => {
        this.msg.warning(error);
        this.loading.set(false);
      },
    });
  }

  /** 组织启用/禁用开关：立即持久化，失败由 service 回滚并重载（上面那条 effect 会跟上回滚）。
   *  启用了才显示真实组织与「创建组织」按钮。 */
  protected onToggleEnabled(enabled: boolean): void {
    this.organizationEnabled.set(enabled);
    const next = new UserSettings();
    next.organizationEnabled = enabled;
    this.account.updateSettings(next);
  }

  cancel(): void {
    this.#modalRef.destroy(undefined);
  }

  /** 选组织：点个人组织回空选中、点真实组织设当前（勾选与 localStorage 由 service 管），
   *  关窗并回首页，让当前组织的切换立即生效在具体页面上。 */
  protected selectOrganization(organization: UserOrganization): void {
    this.account.setOrganization(organization);
    this.#modalRef.destroy(undefined);
    this.router.navigate(['/main']).then(() => {});
  }

  /** 去账号页（退出/设置都在那里），先关弹窗再跳。 */
  protected goAccount(): void {
    this.#modalRef.destroy(undefined);
    this.router.navigate(['/main/account']).then(() => {});
  }

  /** 退出账号：先关弹窗再裸跳 /passport（那个页面的构造函数一进来就清登录态，同账号页退出按钮）。 */
  protected logout(): void {
    this.#modalRef.destroy(undefined);
    this.router.navigate(['/passport']).then(() => {});
  }

  /** 复制用户 ID 到系统剪贴板（同 AccountComponent 的 onCopy 口径）。 */
  protected onCopy(value: string): void {
    this.copying.set(true);
    this.copyToClipboard(value).then((ok) => {
      this.copying.set(false);
      if (ok) {
        this.msg.success(this.translate.instant('复制成功'));
      } else {
        this.msg.warning(this.translate.instant('复制失败'));
      }
    });
  }

  /** 优先 navigator.clipboard，失败（权限拒绝/非安全上下文）时回退 textarea + execCommand */
  private async copyToClipboard(text: string): Promise<boolean> {
    if (navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(text);
        return true;
      } catch {
        // 继续尝试兜底方案
      }
    }
    try {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      const ok = document.execCommand('copy');
      textarea.remove();
      return ok;
    } catch {
      return false;
    }
  }

  /** 卡片「详情」：与账号页同口径 —— 个人组织进账号详情、真实组织进组织详情。先关弹窗再跳，
   *  不然详情页上头会一直压着这个组织切换框。 */
  protected goDetail(organization: UserOrganization): void {
    this.#modalRef.destroy(undefined);
    const target = organization.personal
      ? ['/main/account/detail']
      : ['/main/organization/detail', organization.id];
    this.router.navigate(target).then(() => {});
  }

  /** 弹创建组织对话框（复用账号页那颗），确认后创建并在列表刷新出新卡片。 */
  protected onCreateOrganization(): void {
    const modal = this.modal.create<OrganizationCreateDialogComponent, undefined, OrganizationCreatePayload>({
      nzTitle: this.translate.instant('创建组织'),
      nzWidth: '800px',
      nzContent: OrganizationCreateDialogComponent,
      nzViewContainerRef: this.viewContainerRef,
      nzFooter: [
        { label: this.translate.instant('取消'), onClick: (c) => c!.cancel() },
        {
          label: this.translate.instant('确认'),
          type: 'primary',
          disabled: (c) => !c!.valid(),
          onClick: (c) => c!.ok(),
        },
      ],
    });

    modal.afterClose.subscribe((result) => {
      if (!result) {
        return;
      }
      this.service.createOrganization(result.code, result.name).subscribe({
        next: () => {
          this.msg.success(this.translate.instant('创建成功'));
          this.loadOrganizations();
        },
        error: (error) => this.msg.warning(error),
      });
    });
  }
}